import { useState } from 'react';
import type { ReactNode } from 'react';
import { OFFER_MODES_STRICTEST_FIRST, OPEN_OFFER_POLICY } from '@yourstore/domain';
import type { OfferMode, TenantConfig } from '@yourstore/domain';
import { useAction, useApi } from '../../context';
import { Confirm, Field, Rich } from '../../bits';
import { Panel, useToast } from '../../kit';
import { OfferSimulator } from '../../OfferSimulator';
import { useT } from '../../../messages';
import { Dialog } from './Dialog';
import { modePhrase, ruleOf, ruleSentence } from './rule';
import type { Rule } from './rule';

/**
 * **How the day is booked** — the scheduling rule read as a sentence, with
 * two ways in (console redesign B2). It replaces the always-open Offer
 * simulator card and its raw JSON.
 *
 *  * **Change…** is the everyday edit: which times are offered (the three
 *    modes, in words), the time between clients, the minimum notice and the
 *    same-day cut-off. It sends only what moved — `PUT …/scheduling-policy`
 *    leaves a key it is not sent alone — and when the mode moves it carries
 *    the rest of the stored policy with it, so kept-back hours and ranking set
 *    in the simulator are not wiped by a change of mode.
 *  * **Try a day…** opens the simulator itself, unchanged in what it does, in
 *    a large dialog: a tool opened on purpose, sitting with the owner, rather
 *    than a card every visit scrolls past.
 *
 * Applying from either confirms, and the confirm quotes the new rule as the
 * same sentence (principle 5): the operator reads what clients and partners
 * will be offered before it is true.
 *
 * The step (`slot_grid_min`) is in the sentence but not in the form: the
 * scheduling-policy route does not take it, and it is set with the CLI.
 */
interface Draft {
  mode: OfferMode;
  after: string;
  before: string;
  lead: string;
  cutoff: string;
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function whole(text: string, max: number): number | null {
  if (!/^\d+$/.test(text.trim())) return null;
  const n = Number(text);
  return n <= max ? n : null;
}

export function BookingRule({
  tenantId,
  name,
  timezone,
  config,
  onChanged,
}: {
  tenantId: string;
  name: string;
  timezone: string;
  config: Partial<TenantConfig> | null;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const action = useAction();
  const rule = ruleOf(config);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [trying, setTrying] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const open = (): void => {
    action.clear();
    setDone(null);
    setEditing({
      mode: rule.policy?.mode ?? 'open',
      after: String(rule.after),
      before: String(rule.before),
      lead: String(rule.lead),
      cutoff: rule.cutoff ?? '',
    });
  };

  const after = editing === null ? null : whole(editing.after, 240);
  const before = editing === null ? null : whole(editing.before, 240);
  const lead = editing === null ? null : whole(editing.lead, 43_200);
  const cutoffOk = editing === null || editing.cutoff === '' || TIME.test(editing.cutoff);
  const valid = after !== null && before !== null && lead !== null && cutoffOk;

  /** The rule the draft would leave behind — what the confirm quotes. */
  const next: Rule | null =
    editing === null || !valid
      ? null
      : {
          ...rule,
          after,
          before,
          lead,
          cutoff: editing.cutoff === '' ? null : editing.cutoff,
          policy:
            editing.mode === (rule.policy?.mode ?? 'open')
              ? rule.policy
              : { ...(rule.policy ?? OPEN_OFFER_POLICY), mode: editing.mode },
        };

  const apply = async (): Promise<void> => {
    if (next === null) return;
    const ok = await action.run(async () => {
      await api.setSchedulingPolicy(tenantId, {
        ...(next.after === rule.after ? {} : { default_buffer_after_min: next.after }),
        ...(next.before === rule.before ? {} : { default_buffer_before_min: next.before }),
        ...(next.lead === rule.lead ? {} : { min_lead_minutes: next.lead }),
        ...(next.cutoff === rule.cutoff ? {} : { same_day_cutoff: next.cutoff }),
        ...(next.policy === rule.policy || next.policy === null
          ? {}
          : { offer_policy: next.policy }),
      });
    });
    if (ok) {
      setConfirming(false);
      setEditing(null);
      setDone(t('setup.rule.applied'));
      toast(t('setup.rule.applied'));
      onChanged();
    }
  };

  const unchanged =
    next !== null &&
    next.after === rule.after &&
    next.before === rule.before &&
    next.lead === rule.lead &&
    next.cutoff === rule.cutoff &&
    next.policy === rule.policy;

  return (
    <Panel
      title={t('setup.rule.title')}
      actions={
        <>
          <button type="button" className="button button--sm" onClick={open}>
            {t('setup.rule.change')}
          </button>
          <button
            type="button"
            className="button button--sm"
            onClick={() => {
              setDone(null);
              setTrying(true);
            }}
          >
            {t('setup.rule.try')}
          </button>
        </>
      }
    >
      <p className="sentence">
        <Rich text={ruleSentence(t, name, rule)} />
      </p>
      {done !== null ? <p className="ok">{done}</p> : null}
      <p className="faint">{t('setup.rule.tryNote')}</p>

      {editing !== null ? (
        <Dialog
          title={t('setup.rule.editTitle')}
          busy={action.busy}
          onClose={() => {
            action.clear();
            setEditing(null);
          }}
          footer={
            <button
              type="button"
              className="button button--primary"
              disabled={!valid || unchanged || action.busy}
              onClick={() => {
                action.clear();
                setConfirming(true);
              }}
            >
              {t('setup.rule.apply')}
            </button>
          }
        >
          <fieldset className="rule-modes">
            <legend>{t('setup.rule.modeLegend')}</legend>
            {OFFER_MODES_STRICTEST_FIRST.slice()
              .reverse()
              .map((mode) => (
                <label key={mode}>
                  <input
                    type="radio"
                    name={`${tenantId}-mode`}
                    checked={editing.mode === mode}
                    onChange={() => setEditing({ ...editing, mode })}
                  />
                  <span>{capitalise(modePhrase(t, mode))}</span>
                </label>
              ))}
          </fieldset>
          <div className="facts">
            <Field label={t('setup.rule.gapAfter')}>
              <input
                type="number"
                min={0}
                max={240}
                value={editing.after}
                aria-invalid={after === null}
                onChange={(e) => setEditing({ ...editing, after: e.target.value })}
              />
            </Field>
            <Field label={t('setup.rule.gapBefore')}>
              <input
                type="number"
                min={0}
                max={240}
                value={editing.before}
                aria-invalid={before === null}
                onChange={(e) => setEditing({ ...editing, before: e.target.value })}
              />
            </Field>
            <Field label={t('setup.rule.noticeLabel')} hint={t('setup.rule.noticeHint')}>
              <input
                type="number"
                min={0}
                max={43_200}
                value={editing.lead}
                aria-invalid={lead === null}
                onChange={(e) => setEditing({ ...editing, lead: e.target.value })}
              />
            </Field>
            <Field label={t('setup.rule.cutoffLabel')} hint={t('setup.rule.cutoffHint')}>
              <input
                type="time"
                value={editing.cutoff}
                aria-invalid={!cutoffOk}
                onChange={(e) => setEditing({ ...editing, cutoff: e.target.value })}
              />
            </Field>
          </div>
          {next === null ? null : (
            <p className="sentence sentence--preview">
              <Rich text={ruleSentence(t, name, next)} />
            </p>
          )}
          <p className="faint">{t('setup.rule.stepNote')}</p>
        </Dialog>
      ) : null}

      {confirming && next !== null ? (
        <Confirm
          title={t('setup.rule.confirm.title')}
          consequence={t('setup.rule.confirm.consequence', {
            sentence: ruleSentence(t, name, next),
          })}
          confirmLabel={t('setup.rule.confirm.apply')}
          tone="primary"
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void apply()}
          onCancel={() => {
            action.clear();
            setConfirming(false);
          }}
        />
      ) : null}

      {trying ? (
        <Dialog title={t('sim.title')} size="large" onClose={() => setTrying(false)}>
          <OfferSimulator
            tenantId={tenantId}
            name={name}
            timezone={timezone}
            config={config}
            onApplied={() => {
              setTrying(false);
              setDone(t('setup.rule.applied'));
              onChanged();
            }}
          />
        </Dialog>
      ) : null}
    </Panel>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
